/**
 * /demo — public preview page (no auth) that demonstrates real live data.
 *
 * Two visible value props on one page:
 *   1. Top 10 Ghana stations ranked by live listener count (names + rank +
 *      online dot — exact numbers are gated behind sign-up to preserve the
 *      paywall, mirroring /api/public/top-stations).
 *   2. Full demographic snapshot for the #1 ranked station, pulled from the
 *      free taster endpoint. Visitors get ONE free taster per 24h before the
 *      402 paywall card takes over.
 *
 * Uses the dark broadcast theme (T.dark) so the page reads like the real
 * station dashboard — visitors get a realistic preview of what they unlock.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { NavBar } from "@/components/NavBar";
import { ConfidenceLabel } from "@/components/ConfidenceLabel";
import { T, LSM_BANDS, AGE_BANDS } from "@/lib/theme";

const C = T.dark;
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function Demo() {
  const nav = useNavigate();
  const [pulse, setPulse] = useState({ total_estimated_people: 0, stations_online: 0 });
  const [rankings, setRankings] = useState([]);
  const [taster, setTaster] = useState(null);
  const [paywall, setPaywall] = useState(null); // { message, next_free_call_at }
  const [error, setError] = useState(null);

  // 1. Pulse for the headline number
  useEffect(() => {
    axios.get(`${API_BASE}/realtime/pulse`)
      .then((r) => setPulse(r.data || {}))
      .catch(() => {});
  }, []);

  // 2. Top-10 list + taster for #1
  useEffect(() => {
    (async () => {
      try {
        const top = await axios.get(`${API_BASE}/public/top-stations?country=Ghana&limit=10`);
        const list = top.data.rankings || [];
        setRankings(list);
        if (list.length > 0) {
          try {
            const t = await axios.get(`${API_BASE}/free/taster/audience/${list[0].station_id}`);
            setTaster(t.data);
          } catch (e) {
            if (e.response?.status === 402) {
              setPaywall(e.response.data.detail || {});
            }
          }
        }
      } catch (e) {
        setError("Could not load live data right now.");
      }
    })();
  }, []);

  const total = pulse.total_estimated_people || 0;
  const featured = rankings[0];
  const demo = taster?.demographics || {};
  const gender = demo.gender || {};
  const age = demo.age || {};
  const lsm = demo.lsm || {};

  return (
    <div data-testid="demo-page" style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Inter',sans-serif", color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');
        body{margin:0;background:${C.bg}}
        @keyframes pulseG{0%{box-shadow:0 0 0 0 #10B98199}70%{box-shadow:0 0 0 8px #10B98100}100%{box-shadow:0 0 0 0 #10B98100}}`}</style>
      <NavBar dark={true} />

      {/* Hero */}
      <div style={{ padding: "56px 24px 30px", maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.cyanDim, color: C.cyan,
          padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, marginBottom: 18, letterSpacing: "0.08em" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.cyan, animation: "pulseG 1.8s ease-out infinite" }} />
          LIVE DEMO · NO SIGNUP REQUIRED
        </div>
        <h1 style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 800, lineHeight: 1.1, marginBottom: 14 }}>
          Live right now across <span style={{ color: C.cyan }}>Ghana</span>
        </h1>
        <div data-testid="demo-total-listeners" style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(40px,7vw,64px)", fontWeight: 800, color: C.cyan, lineHeight: 1, marginBottom: 6 }}>
          {total.toLocaleString()}
        </div>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 4 }}>estimated individual listeners — updated every 60 seconds</p>
        <p style={{ color: C.muted, fontSize: 12 }}>{pulse.stations_online || 0} stations online</p>
      </div>

      {/* Top 10 list */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 40px", display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }} data-testid="demo-top10">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Top 10 Ghana stations — live</h2>
              <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>Ranked by estimated audience. Listener numbers shown after sign-up.</p>
            </div>
            <Link to="/register" data-testid="demo-cta-top-signup"
              style={{ background: C.cyan, color: C.bg, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 12, textDecoration: "none" }}>
              See all numbers →
            </Link>
          </div>
          {error && <div style={{ color: C.muted, fontSize: 14, padding: 20, textAlign: "center" }}>{error}</div>}
          {rankings.map((r, i) => (
            <div key={r.station_id} data-testid={`demo-rank-${r.rank}`}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: 8, marginBottom: 4,
                background: i === 0 ? C.cyanDim : i % 2 === 0 ? C.surface : "transparent",
                border: i === 0 ? `1px solid ${C.cyan}33` : "1px solid transparent" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: i === 0 ? C.cyan : C.border, color: i === 0 ? C.bg : C.muted,
                fontSize: 11, fontWeight: 700 }}>{r.rank}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: i === 0 ? C.cyan : C.text, fontWeight: i === 0 ? 700 : 500, fontSize: 13 }}>{r.name}</span>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: r.is_online ? "#10B981" : "#EF4444",
                    animation: r.is_online ? "pulseG 1.8s ease-out infinite" : "none" }} />
                </div>
                {r.now_playing && (
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Now: {r.now_playing}
                  </div>
                )}
              </div>
              <div style={{ color: C.muted, fontSize: 11, fontStyle: "italic" }}>
                {i === 0 ? "← snapshot below" : "Sign up to reveal"}
              </div>
            </div>
          ))}
          {rankings.length === 0 && !error && (
            <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Live rankings load after the first crawl cycle…</div>
          )}
        </div>

        {/* Featured station demographic snapshot */}
        {featured && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }} data-testid="demo-featured-snapshot">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>#{featured.rank} · live snapshot</div>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
                  {featured.name}{" "}
                  <ConfidenceLabel source={taster?.confidence_label || "MODEL"} size="xs" />
                </h2>
              </div>
              {taster?.taster?.calls_remaining_today === 0 && (
                <span style={{ background: "#F59E0B22", color: "#FBBF24", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                  free taster used
                </span>
              )}
            </div>

            {paywall ? (
              <div data-testid="demo-paywall" style={{ background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 12, padding: 28, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
                <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>You&apos;ve used your free taster for today</h3>
                <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px", maxWidth: 380, marginInline: "auto" }}>
                  Sign up free to see live demographics for every tracked station — no card required.
                </p>
                <Link to="/register" data-testid="demo-paywall-cta"
                  style={{ background: C.cyan, color: C.bg, padding: "10px 22px", borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
                  Create free account →
                </Link>
              </div>
            ) : taster ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 18 }}>
                {/* Numbers row */}
                <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
                  {[
                    { label: "Est. Total Listeners", value: (taster.estimated_total_people || 0).toLocaleString(), accent: C.cyan },
                    { label: "Online Now", value: (taster.estimated_online_people || 0).toLocaleString(), accent: "#10B981" },
                    { label: "Male", value: `${Math.round((gender.male?.pct || 0) * 100)}%`, accent: "#3B82F6" },
                    { label: "Female", value: `${Math.round((gender.female?.pct || 0) * 100)}%`, accent: "#EC4899" },
                  ].map((c, i) => (
                    <div key={i} data-testid={`demo-stat-${i}`} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ color: C.muted, fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>{c.label}</div>
                      <div style={{ color: c.accent, fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700 }}>{c.value}</div>
                    </div>
                  ))}
                </div>

                {/* Age */}
                <div data-testid="demo-age" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Age breakdown</div>
                  {AGE_BANDS.map((b) => {
                    const d = age[b.key] || { pct: 0 };
                    return (
                      <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ color: C.muted, fontSize: 11, width: 36, flexShrink: 0 }}>{b.label}</span>
                        <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${(d.pct || 0) * 100}%`, height: "100%", background: b.color, borderRadius: 3 }} />
                        </div>
                        <span style={{ color: b.color, fontSize: 11, fontWeight: 600, width: 36, textAlign: "right" }}>{Math.round((d.pct || 0) * 100)}%</span>
                      </div>
                    );
                  })}
                </div>

                {/* LSM */}
                <div data-testid="demo-lsm" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>LSM (Living Standards)</div>
                  {LSM_BANDS.map((band) => {
                    const d = lsm[band.key] || { pct: 0 };
                    return (
                      <div key={band.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: band.color, flexShrink: 0 }} />
                        <span style={{ color: C.text, fontSize: 11, flex: 1 }}>{band.label}</span>
                        <span style={{ color: band.color, fontSize: 12, fontWeight: 700 }}>{Math.round((d.pct || 0) * 100)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Loading snapshot…</div>
            )}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: "48px 24px", textAlign: "center" }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: "0 0 8px" }}>
          This is real data.
        </h2>
        <p style={{ color: C.muted, fontSize: 15, margin: "0 auto 22px", maxWidth: 520, lineHeight: 1.6 }}>
          Live listener counts, full demographic breakdowns, and ad-verification certificates — for every tracked station across West Africa.
        </p>
        <button onClick={() => nav("/register")} data-testid="demo-cta-signup"
          style={{ background: C.cyan, color: C.bg, padding: "14px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
          Sign up free to track your station →
        </button>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 12 }}>
          No credit card · No commitment
        </div>
      </div>
    </div>
  );
}
